import {
  Fragment,
  type CSSProperties,
  type FC,
  type KeyboardEvent,
} from "react";
import { Tooltip } from "antd";
import { EyeInvisibleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { formatWithTwoDecimals } from "../../../../shared/utils/numberFormatting";
import { createNutrientPlotColorScale } from "../../../../shared/utils/nutrientPlotColors";
import NumberInput from "../NumberInput";
import { ReactComponent as ChevronRightIcon } from "../../../../assets/images/chevron-right-18.svg";
import { ReactComponent as ExpandMoreIcon } from "../../../../assets/images/expand-more-18.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/images/info-14.svg";
import { ReactComponent as EyeIcon } from "../../../../assets/images/remove-red-eye-16.svg";
import "./style.less";

export interface NutrientTableRow {
  id: string;
  label: string;
  applyPercent: number | null;
  productKgHa: number | null;
  productKg: number | null;
  nutrientKgHa: number | null;
  nutrientKg: number | null;
}

export interface NutrientTableProps {
  title: string;
  rowLabel: string;
  groupColumnLabel?: string;
  nutrientLabel: string;
  rows: NutrientTableRow[];
  // Include all groups so table accents use the same scale as the map.
  colorScaleRates?: Array<number | null | undefined>;
  expanded: boolean;
  onToggle: () => void;
  onApplyChange?: (rowId: string, value: number | null) => void;
  onTogglePlot?: (rowId: string) => void;
  // Plots switched on with the eye button (outlined on the map).
  highlightedIds?: string[];
  // Translated apply-rate errors, by row id.
  errors?: Record<string, string[]>;
  highlightMissing?: boolean;
  className?: string;
}

// Whole numbers only: block any typed character that is not a digit, but leave
// named keys (Backspace, arrows, Tab...) and shortcuts alone.
const restrictToIntegers = (event: KeyboardEvent<HTMLInputElement>): void => {
  const { key, ctrlKey, metaKey } = event;
  if (ctrlKey || metaKey || key.length > 1) {
    return;
  }
  if (!/^\d$/.test(key)) {
    event.preventDefault();
  }
};

const formatAmount = (value: number | null): string =>
  value === null ? "-" : formatWithTwoDecimals(value);

interface NutrientTableStyle extends CSSProperties {
  "--rd-amount-width": string;
}

interface AmountHeaderProps {
  label: string;
  tooltip: string;
  perHaUnit: string;
  totalUnit: string;
  className?: string;
}

const AmountHeader: FC<AmountHeaderProps> = ({
  label,
  tooltip,
  perHaUnit,
  totalUnit,
  className,
}) => (
  <div
    className={`rd-nutrient-table__sub-header-cell rd-nutrient-table__col--amount ${
      className ?? ""
    }`}
    role="columnheader"
  >
    <span className="rd-nutrient-table__sub-header-title rd-nutrient-table__sub-header-title--padded">
      {label}
      <Tooltip title={tooltip}>
        <span
          className="rd-nutrient-table__info-icon"
          tabIndex={0}
          aria-label={tooltip}
          role="img"
        >
          <InfoIcon aria-hidden />
        </span>
      </Tooltip>
    </span>
    <span className="rd-nutrient-table__amount-pair">
      <span className="rd-nutrient-table__sub-header-unit rd-nutrient-table__sub-header-unit--padded">
        {perHaUnit}
      </span>
      <span className="rd-nutrient-table__sub-header-unit rd-nutrient-table__sub-header-unit--padded">
        {totalUnit}
      </span>
    </span>
  </div>
);

const NutrientTable: FC<NutrientTableProps> = ({
  title,
  rowLabel,
  groupColumnLabel,
  nutrientLabel,
  rows,
  colorScaleRates,
  expanded,
  onToggle,
  onApplyChange,
  onTogglePlot,
  highlightedIds,
  errors,
  highlightMissing = false,
  className,
}) => {
  const { t } = useTranslation();
  const perHaUnit = formatTranslation(
    t("newRatesAndDosage.nutrientTable.perHaUnit")
  );
  const totalUnit = formatTranslation(
    t("newRatesAndDosage.nutrientTable.totalUnit")
  );
  const CollapseIcon = expanded ? ExpandMoreIcon : ChevronRightIcon;
  const getPlotColor = createNutrientPlotColorScale(
    colorScaleRates ?? rows.map((row) => row.applyPercent)
  );
  const amountCharacters = rows.reduce(
    (longest, row) =>
      Math.max(
        longest,
        formatAmount(row.productKgHa).length,
        formatAmount(row.productKg).length,
        formatAmount(row.nutrientKgHa).length,
        formatAmount(row.nutrientKg).length
      ),
    3
  );
  // Reserve space for two complete amounts, their cell padding and group padding.
  const tableStyle: NutrientTableStyle = {
    "--rd-amount-width": `max(134px, calc(${amountCharacters * 2}ch + 64px))`,
  };

  return (
    <div
      className={`rd-nutrient-table ${className ?? ""}`}
      data-testid="nutrient-table"
    >
      <button
        type="button"
        className="rd-nutrient-table__collapse-header"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="rd-nutrient-table__collapse-icon" aria-hidden="true">
          <CollapseIcon />
        </span>
        <span className="rd-nutrient-table__collapse-label">{title}</span>
      </button>
      {expanded && (
        <div
          className="rd-nutrient-table__grid"
          style={tableStyle}
          role="table"
          aria-label={title}
        >
          <div className="rd-nutrient-table__group-header" role="row">
            <span
              className="rd-nutrient-table__group-header-cell rd-nutrient-table__group-col--plots"
              role="columnheader"
            >
              {groupColumnLabel ??
                formatTranslation(t("newRatesAndDosage.nutrientTable.plots"))}
            </span>
            <span
              className="rd-nutrient-table__group-header-cell rd-nutrient-table__group-col--rate"
              role="columnheader"
            >
              {formatTranslation(t("newRatesAndDosage.nutrientTable.rate"))}
            </span>
            <span
              className="rd-nutrient-table__group-header-cell rd-nutrient-table__group-col--amount"
              role="columnheader"
            >
              {formatTranslation(t("newRatesAndDosage.nutrientTable.amount"))}
            </span>
          </div>
          <div className="rd-nutrient-table__sub-header" role="row">
            <div
              className="rd-nutrient-table__sub-header-cell rd-nutrient-table__col--label"
              role="columnheader"
            >
              <span className="rd-nutrient-table__sub-header-title">
                {rowLabel}
              </span>
              <span className="rd-nutrient-table__sub-header-unit" />
            </div>
            <div
              className="rd-nutrient-table__sub-header-cell rd-nutrient-table__col--apply"
              role="columnheader"
            >
              <span className="rd-nutrient-table__sub-header-title">
                {formatTranslation(t("newRatesAndDosage.nutrientTable.apply"))}
              </span>
              <span className="rd-nutrient-table__sub-header-unit">
                {formatTranslation(
                  t("newRatesAndDosage.nutrientTable.applyUnit")
                )}
              </span>
            </div>
            <AmountHeader
              label={formatTranslation(
                t("newRatesAndDosage.nutrientTable.product")
              )}
              tooltip={formatTranslation(
                t("newRatesAndDosage.nutrientTable.productTooltip")
              )}
              perHaUnit={perHaUnit}
              totalUnit={totalUnit}
            />
            <AmountHeader
              label={nutrientLabel}
              tooltip={formatTranslation(
                t("newRatesAndDosage.nutrientTable.nutrientTooltip")
              )}
              perHaUnit={perHaUnit}
              totalUnit={totalUnit}
              className="rd-nutrient-table__sub-header-cell--nutrient"
            />
            <span className="rd-nutrient-table__col--action" />
          </div>
          <div className="rd-nutrient-table__body">
            {rows.map((row) => (
              <Fragment key={row.id}>
                <div
                  className="rd-nutrient-table__row"
                  role="row"
                  data-testid="nutrient-table-row"
                >
                  <div
                    className="rd-nutrient-table__cell rd-nutrient-table__col--label"
                    role="cell"
                  >
                    <span
                      className="rd-nutrient-table__accent"
                      style={{
                        backgroundColor: getPlotColor(row.applyPercent),
                      }}
                      aria-hidden="true"
                    />
                    <span className="rd-nutrient-table__row-label">
                      {row.label}
                    </span>
                  </div>
                  <div
                    className="rd-nutrient-table__cell rd-nutrient-table__col--apply"
                    role="cell"
                  >
                    <NumberInput
                      className="rd-nutrient-table__apply-input"
                      size="small"
                      min={0}
                      precision={0}
                      step={1}
                      value={row.applyPercent}
                      status={
                        (highlightMissing && row.applyPercent === null) ||
                        errors?.[row.id]?.length
                          ? "error"
                          : undefined
                      }
                      onKeyDown={restrictToIntegers}
                      onChange={(value) => onApplyChange?.(row.id, value)}
                      aria-label={formatTranslation(
                        t("newRatesAndDosage.nutrientTable.applyInput", {
                          row: row.label,
                        })
                      )}
                    />
                  </div>
                  <div
                    className="rd-nutrient-table__cell rd-nutrient-table__col--amount"
                    role="cell"
                  >
                    <span
                      className="rd-nutrient-table__value"
                      title={formatAmount(row.productKgHa)}
                    >
                      {formatAmount(row.productKgHa)}
                    </span>
                    <span
                      className="rd-nutrient-table__value rd-nutrient-table__value--muted"
                      title={formatAmount(row.productKg)}
                    >
                      {formatAmount(row.productKg)}
                    </span>
                  </div>
                  <div
                    className="rd-nutrient-table__cell rd-nutrient-table__col--amount"
                    role="cell"
                  >
                    <span
                      className="rd-nutrient-table__value"
                      title={formatAmount(row.nutrientKgHa)}
                    >
                      {formatAmount(row.nutrientKgHa)}
                    </span>
                    <span
                      className="rd-nutrient-table__value rd-nutrient-table__value--muted"
                      title={formatAmount(row.nutrientKg)}
                    >
                      {formatAmount(row.nutrientKg)}
                    </span>
                  </div>
                  <div
                    className="rd-nutrient-table__cell rd-nutrient-table__col--action"
                    role="cell"
                  >
                    <button
                      type="button"
                      className={`rd-nutrient-table__show-btn ${
                        highlightedIds?.includes(row.id) ?? true
                          ? "rd-nutrient-table__show-btn--active"
                          : ""
                      }`}
                      aria-pressed={highlightedIds?.includes(row.id) ?? true}
                      aria-label={formatTranslation(
                        t("newRatesAndDosage.nutrientTable.showPlot", {
                          row: row.label,
                        })
                      )}
                      onClick={() => onTogglePlot?.(row.id)}
                    >
                      {highlightedIds?.includes(row.id) ?? true ? (
                        <EyeIcon aria-hidden />
                      ) : (
                        <EyeInvisibleOutlined aria-hidden />
                      )}
                    </button>
                  </div>
                </div>
                {errors?.[row.id]?.map((message) => (
                  <span
                    key={message}
                    className="rd-nutrient-table__row-error"
                    role="alert"
                  >
                    {message}
                  </span>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NutrientTable;
